import type { CompanyContentBoostType } from "@/modules/companies/client/api/contentBoostTypes"
import type { FunctionComponent } from "react"

import classNames from "classnames"

import { Divider } from "@jobteaser/spark/components/Divider"

import { Push } from "@/modules/aiCoverLetter/components/Push/Push"
import { AI_COVER_LETTER, AI_COVER_LETTER_VARIANTS } from "@/modules/aiCoverLetter/constants/flags"
import { useCareerCenterFetch } from "@/modules/careerCenter/hooks/useCareerCenterFetch"
import { CompanyContentBoost } from "@/modules/companies/client/components/CompanyContentBoost/CompanyContentBoost"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { PrivateOfferButton } from "@/modules/jobAds/client/components/PrivateOfferButton/PrivateOfferButton"
import { RssFeed } from "@/modules/jobAds/client/components/RssFeed/RssFeed"
import { SavedSearch } from "@/modules/jobAds/client/components/SavedSearch/SavedSearch"
import { PRIVATE_OFFER_PATH, PRIVATE_OFFER_PATH_RECRUITER } from "@/modules/jobAds/client/constants"
import { JOB_ADS_CONTENT_BOOST } from "@/modules/jobAds/client/constants/flags"
import { useJobAdsFromAlgolia } from "@/modules/jobAds/client/hooks/useJobAdsFromAlgolia"
import { usePageContext } from "@/modules/jobSearch/components/PageContext/PageContextProvider"
import { PrefilteredAside } from "@/modules/jobSearchPrefiltered/components/PrefilteredAside/PrefilteredAside"
import { usePrefilteredJobSearch } from "@/modules/jobSearchPrefiltered/hooks/usePrefilteredJobSearch"
import { LayoutChild } from "@/modules/layouts/components/Layout/LayoutChild"
import { useLocale } from "@/modules/locales/useLocale"
import { useFlag } from "@/modules/unleash/hooks/useFlag"
import { useCurrentUserFetch } from "@/modules/user/client/hooks/useCurrentUserFetch"

import styles from "./JobAdsAside.module.css"

export const JobAdsAside: FunctionComponent = () => {
  const { t } = useTranslation(["ai_cover_letter", "job_ads_fo"])
  const locale = useLocale()
  const isJobAdsContentBoostActive = useFlag(JOB_ADS_CONTENT_BOOST)
  const isAICoverLetterPushVisible = useFlag(AI_COVER_LETTER, AI_COVER_LETTER_VARIANTS.ENABLED)
  const { dataJobAds, isErrorJobAds } = useJobAdsFromAlgolia<CompanyContentBoostType>()
  const isPrefilteredJobSearchPage = usePrefilteredJobSearch()
  const { dataCareerCenter } = useCareerCenterFetch()
  const { dataCurrentUser } = useCurrentUserFetch()
  const { savedSearch } = usePageContext()

  const hasUserSavedSearches = !!savedSearch
  const hasCcRssFeed = Boolean(dataCareerCenter?.careerCenterConf?.modules?.hasRssFeed)
  const isUserSuperAdmin = Boolean(dataCurrentUser?.attributes?.isCcSuperAdmin)
  const contentBoost = dataJobAds && !!dataJobAds.algoliaCustomData?.length ? dataJobAds?.algoliaCustomData[0] : null
  const algoliaCustomDataUuid = contentBoost?.ruleUuid
  const isContentBoostVisible = isJobAdsContentBoostActive && !!contentBoost
  const isPrivateOfferButtonVisible = dataCareerCenter?.careerCenterConf?.postOfferFromFrontOffice || false
  const isRssFeedVisible = isUserSuperAdmin && hasCcRssFeed
  const isSavedSearchVisible = !isErrorJobAds && dataCurrentUser?.isLogged && hasUserSavedSearches

  const hasAside =
    isSavedSearchVisible ||
    isPrefilteredJobSearchPage ||
    isRssFeedVisible ||
    isPrivateOfferButtonVisible ||
    isContentBoostVisible ||
    isAICoverLetterPushVisible

  return (
    hasAside && (
      <LayoutChild
        className={classNames(styles.aside, {
          [styles.aside___isPrefilteredJobSearchPage]: isPrefilteredJobSearchPage,
          [styles.aside___isRssFeedVisible]: isRssFeedVisible,
          [styles.aside___isPrivateOfferButtonVisible]: isPrivateOfferButtonVisible,
          [styles.aside___isContentBoostVisible]: isContentBoostVisible,
          [styles.aside___hasNotSavedSearches]: !hasUserSavedSearches,
        })}
        from={9}
        to={12}
      >
        {isSavedSearchVisible && <SavedSearch className={styles.savedSearches} />}

        {isAICoverLetterPushVisible && (
          <>
            <Divider className={styles.aside__divider} />
            <Push locale={locale} badgeLabel={t("ai_cover_letter.push.badge")} text={t("ai_cover_letter.push.text")} />
          </>
        )}

        {isPrefilteredJobSearchPage && <PrefilteredAside jobAdTotalSize={dataJobAds?.totalSize || 0} />}
        {isContentBoostVisible && (
          <>
            <Divider className={styles.aside__divider} />
            <CompanyContentBoost
              algoliaRuleUuid={algoliaCustomDataUuid}
              contentBoost={contentBoost}
              trackingModule="fo_job_search"
            />
          </>
        )}
        {isRssFeedVisible && (
          <>
            <Divider className={styles.aside__divider} />
            <RssFeed
              pasteSuccessLabel={t("job_ads_fo.client.search.rss_feed.paste_success_label")}
              title={t("job_ads_fo.client.search.rss_feed.title")}
              clearIconAriaLabel={t("job_ads_fo.client.search.rss_feed.clear_button_label")}
              errorLabel={t("job_ads_fo.client.search.rss_feed.error_label")}
              inputLabel={t("job_ads_fo.client.search.rss_feed.select_label")}
              placeholder={t("job_ads_fo.client.search.rss_feed.placeholder")}
              submitLabel={t("job_ads_fo.client.search.rss_feed.paste_label")}
              closeModalLabel={t("job_ads_fo.client.search.rss_feed.modal.close_button")}
              titleModal={t("job_ads_fo.client.search.rss_feed.modal.title")}
              detailModal={t("job_ads_fo.client.search.rss_feed.modal.detail")}
            />
          </>
        )}
        {isPrivateOfferButtonVisible && (
          <>
            <Divider className={styles.aside__divider} />
            <PrivateOfferButton
              href={
                dataCurrentUser?.attributes?.roleTitle === "student_recruiter"
                  ? `/${locale}${PRIVATE_OFFER_PATH_RECRUITER}`
                  : `/${locale}${PRIVATE_OFFER_PATH}`
              }
            >
              {t("job_ads_fo.client.search.private_offer_button.label")}
            </PrivateOfferButton>
          </>
        )}
      </LayoutChild>
    )
  )
}
